import React, { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Trophy, Flame, Activity, Target, Crown, RefreshCw, Award, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { AVATAR_REGISTRY, customAvatarSrc } from "../lib/avatars";
import { useTranslation } from "react-i18next";

export default function Leaderboard() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const [sort, setSort] = useState("score");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const reqIdRef = useRef(0);

  const SORTS = [
    { key: "score", label: t("leaderboard.sort_score", "Score"), icon: Award },
    { key: "wins", label: t("leaderboard.sort_wins"), icon: Trophy },
    { key: "streak", label: t("leaderboard.sort_streak"), icon: Flame },
    { key: "calories", label: t("leaderboard.sort_calories"), icon: Activity },
    { key: "minutes", label: t("leaderboard.sort_minutes", "Minutes"), icon: Clock },
    { key: "levels", label: t("leaderboard.sort_levels"), icon: Target },
  ];

  const load = useCallback(async () => {
    const reqId = ++reqIdRef.current;
    setError(null);
    setData(null);
    try {
      const { data } = await api.get(`/leaderboard/global?sort=${sort}&limit=50`);
      // Ignore out-of-order responses — only the latest requested sort wins
      // (a slow earlier fetch must not overwrite the current tab's data).
      if (reqId === reqIdRef.current) setData(data);
    } catch (e) {
      if (reqId === reqIdRef.current) {
        setError(e?.response?.data?.detail || e?.message || t("leaderboard.couldnt_load"));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  useEffect(() => { load(); }, [load]);

  const valueFor = (row) => {
    if (sort === "score") return (row.fitness_score || 0).toLocaleString();
    if (sort === "wins") return row.total_wins;
    if (sort === "streak") return row.longest_streak;
    if (sort === "calories") return row.calories_burned;
    if (sort === "minutes") return (row.minutes_played || 0).toLocaleString();
    return row.fitness_level;
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <p className="text-xs uppercase tracking-[0.25em] text-[#8AA48F] mb-1.5">{t("leaderboard.global")}</p>
        <h1 style={{ fontFamily: "Outfit, sans-serif" }} className="text-2xl sm:text-3xl font-light tracking-tight">
          {t("leaderboard.headline")}
        </h1>
        <p className="text-sm text-[#2C332A]/70 mt-2 max-w-xl mx-auto">
          {t("leaderboard.intro")}
        </p>
      </div>

      <div className="flex flex-wrap justify-center gap-2">
        {SORTS.map((s) => (
          <Button
            key={s.key}
            data-testid={`sort-${s.key}`}
            onClick={() => setSort(s.key)}
            variant={sort === s.key ? "default" : "outline"}
            className={`rounded-full ${sort === s.key ? "bg-[#5F7161] hover:bg-[#4D5D4F] text-white" : "border-[#E6E2D8]"}`}
          >
            <s.icon size={14} strokeWidth={1.5} className="mr-2" /> {s.label}
          </Button>
        ))}
      </div>

      <div className="rounded-3xl bg-white border border-[#E6E2D8] overflow-hidden">
        {error ? (
          <div className="p-6 text-center" data-testid="leaderboard-error">
            <p className="text-sm text-[#A0454E] mb-3">{error}</p>
            <Button onClick={load} variant="outline" className="rounded-full" data-testid="leaderboard-retry">
              <RefreshCw size={14} strokeWidth={1.6} className="mr-1.5" /> {t("leaderboard.try_again")}
            </Button>
          </div>
        ) : !data ? (
          <div className="p-4 space-y-2 animate-pulse" data-testid="leaderboard-skeleton">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-14 rounded-xl bg-[#EFEBE0]" />
            ))}
          </div>
        ) : data.leaders.length === 0 ? (
          <p className="p-8 text-[#2C332A]/60">{t("leaderboard.no_players")}</p>
        ) : (
          <div className="divide-y divide-[#E6E2D8]">
            {data.leaders.map((row) => (
              <div
                key={row.user_id}
                data-testid={`leader-${row.rank}`}
                className={`flex items-center justify-between p-4 ${row.is_me ? "bg-[#F1EEE6]" : ""}`}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="relative shrink-0">
                    <div
                      className={`w-12 h-12 rounded-full bg-cover bg-center ring-2 ${
                        row.rank === 1 ? "ring-[#E8A365]" :
                        row.rank === 2 ? "ring-[#D0A98F]" :
                        row.rank === 3 ? "ring-[#8AA48F]" :
                        "ring-[#E6E2D8]"
                      }`}
                      style={{
                        backgroundImage: `url(${customAvatarSrc(row) || (AVATAR_REGISTRY[row.selected_avatar] || AVATAR_REGISTRY.runner).img})`,
                      }}
                    />
                    {row.rank === 1 && (
                      <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                        <Crown size={18} strokeWidth={2.2} className="text-[#E8A365] drop-shadow-md" fill="#E8A365" />
                      </div>
                    )}
                    <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold tabular-nums shadow ${
                      row.rank === 1 ? "bg-[#E8A365] text-white" :
                      row.rank === 2 ? "bg-[#D0A98F] text-white" :
                      row.rank === 3 ? "bg-[#8AA48F] text-white" :
                      "bg-white text-[#2C332A] border border-[#E6E2D8]"
                    }`}>
                      {row.rank}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-sm sm:text-base flex flex-wrap items-center gap-x-1.5 leading-snug">
                      <span className="break-words">{row.name}</span>
                      {(row.weekly_champion_count || 0) >= 3 && (
                        <span
                          data-testid={`champion-crown-${row.rank}`}
                          title={t("leaderboard.champion_tooltip", { count: row.weekly_champion_count })}
                          className="text-base shrink-0"
                        >👑</span>
                      )}
                      {row.is_me && <span className="ml-1 text-xs text-[#5F7161]">{t("leaderboard.you")}</span>}
                    </p>
                    <p className="text-xs text-[#2C332A]/60">
                      {t("leaderboard.stats_row", { wins: row.total_wins, streak: row.longest_streak, levels: row.fitness_level })}
                    </p>
                  </div>
                </div>
                <p style={{ fontFamily: "Outfit, sans-serif" }} className="text-2xl font-light tabular-nums whitespace-nowrap shrink-0 pl-3">
                  {valueFor(row)}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {data && !data.leaders.some((r) => r.is_me) && (
        <div className="p-4 rounded-2xl bg-[#F1EEE6] border border-[#E6E2D8] text-sm text-[#2C332A]/70">
          {t("leaderboard.not_on_board")}
          <Link to="/journey" className="text-[#5F7161] underline">{t("leaderboard.play_a_level")}</Link>{t("leaderboard.first_win_ranks")}
        </div>
      )}
    </div>
  );
}
